<template>
  <CustomModal ref="modal" title="Encaissements" class="add-collection-modal">
    <div class="modal-content">
      <CustomForm ref="form" :inputs=inputs :submit=buttons.submit @submit="preventNativeSubmit">
        <div class="fields-row">
          <CustomTextField class="amount" :content=inputs.amount />
          <CustomTextField class="date" :content=inputs.date />
        </div>
        <div class="fields-row">
          <CustomSelectField class="payment-type" :content=inputs.paymentType />
          <CustomSelectField class="select" :content=inputs.paymentPurpose />
        </div>
        <div v-if="isCreditNotePurpose" class="fields-row">
          <CustomSelectField class="select" :content=inputs.creditNoteNumber />
        </div>
        <div class="button-container">
          <CustomButton class="button" @click="submitForm" :params=buttons.submit />
          <CustomButton class="button" :is-secondary="true" @click="closeModal()" :params=buttons.cancel />
        </div>
      </CustomForm>
      <div class="history">
        <p class="section-label">Historique</p>
        <CollectionTable ref="collectionTable" class="table" :callback="onHistoryEdited"/>
      </div>
    </div>
  </CustomModal>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import CustomSelectField from "@/components/forms/customs/customSelectField/CustomSelectField.vue";
import {NotificationService} from "@vues/v-common";
import { PAYMENT_TYPE } from "@/models/paymentType";
import {CollectionController} from "@/controllers/business/CollectionController";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import CollectionTable from "@/components/tables/CollectionTable.vue";
import {PAYMENT_PURPOSE} from "@/models/paymentPurpose";
import {convertISOToDate} from "@/utils";
library.add(faXmark);

export default {
  name: 'AddCollectionModal',
  props: ['callback'],
  components: {CollectionTable, CustomModal, CustomForm, CustomSelectField, CustomTextField, CustomButton},
  data() {
    return {
      businessId: undefined,
      inputs: {
        amount: {label: 'Montant', value: 0, type: 'number'},
        paymentType: {label: 'Mode de paiement', value: PAYMENT_TYPE.TRANSFER.value, type: 'select', selectValues: [PAYMENT_TYPE.CHECK, PAYMENT_TYPE.CASH, PAYMENT_TYPE.TRANSFER]},
        paymentPurpose: {label: 'Motif', value: PAYMENT_PURPOSE.INVOICE.value, type: 'select', selectValues: [PAYMENT_PURPOSE.INVOICE, PAYMENT_PURPOSE.DEPOSIT, PAYMENT_PURPOSE.CREDIT_NOTE]},
        creditNoteNumber: {label: 'Avoir', value: undefined, type: 'select', selectValues: []},
        date: {label: 'Date', value: convertISOToDate(new Date()), type: 'date'},
      },
      buttons:{
        submit: {text: "Encaisser"},
        cancel: {text: "Annuler"},
      }
    };
  },
  computed: {
    isCreditNotePurpose() {
      return this.inputs.paymentPurpose.value === PAYMENT_PURPOSE.CREDIT_NOTE.value;
    }
  },
  methods: {
    // business : le contrat (ou son id) sur lequel enregistrer l'encaissement
    openModal(business) {
      const businessId = (business && business.id) ? business.id : business;
      const creditNotes = (business && business.creditNotes) ? business.creditNotes : [];
      this.$refs.modal.openModal();
      this.businessId = businessId;
      this.inputs.amount.value = undefined;
      this.inputs.paymentType.value = PAYMENT_TYPE.TRANSFER.value;
      this.inputs.paymentPurpose.value = PAYMENT_PURPOSE.INVOICE.value;
      this.inputs.creditNoteNumber.selectValues = creditNotes.map(creditNote => ({
        label: 'Avoir AV' + String(creditNote.number).padStart(4, '0'),
        value: creditNote.number
      }));
      this.inputs.creditNoteNumber.value = creditNotes.length === 1 ? creditNotes[0].number : undefined;
      this.$refs.collectionTable.$refs.table.$refs.paginationArray.init(0,5);
      this.$refs.collectionTable.retrieveData(businessId)
    },
    closeModal(event = false) {
      this.$refs.modal?.closeModal(event);
    },
    // Un encaissement de l'historique a été modifié : le parent recharge le contrat (statuts potentiellement impactés)
    onHistoryEdited() {
      this.closeModal(true);
      this.callback();
    },
    preventNativeSubmit(event) {
      event?.preventDefault?.();
    },
    async submitForm(event) {
      this.preventNativeSubmit(event);
      if(!this.inputs.amount.value){
        this.inputs.amount.error = true;
        (new NotificationService).addNotification("danger","Le montant n'est pas renseigné");
        return;
      }

      if(!this.inputs.paymentType.value){
        this.inputs.paymentType.error = true;
        (new NotificationService).addNotification("danger","Le type de paiement n'est pas renseigné");
        return;
      }

      if(!this.inputs.paymentPurpose.value){
        this.inputs.paymentPurpose.error = true;
        (new NotificationService).addNotification("danger","Le but du paiement n'est pas renseigné");
        return;
      }

      if(this.isCreditNotePurpose && this.inputs.creditNoteNumber.value === undefined){
        this.inputs.creditNoteNumber.error = true;
        (new NotificationService).addNotification("danger","L'avoir concerné n'est pas renseigné");
        return;
      }

      const res = await this.$refs.form.submitForm(() => CollectionController.post({
        amount: this.inputs.amount.value,
        paymentType: this.inputs.paymentType.value,
        paymentPurpose: this.inputs.paymentPurpose.value,
        creditNoteNumber: this.isCreditNotePurpose ? this.inputs.creditNoteNumber.value : undefined,
        date: this.inputs.date.value,
        businessId: this.businessId
      }));
      if (!res.errors) {
        (new NotificationService).addNotification("success","Encaissement réalisé");
        this.closeModal(true);
        this.callback();
      }
    }
  }
}

</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

// Pas de classe « container » ici : le style scoped du parent s'appliquerait à la racine de CustomSelectField (qui porte cette classe) et décalerait les listes déroulantes
.modal-content
  display flex
  flex-direction column
  gap 14px

  form
    display flex
    flex-direction column
    gap 10px

    .fields-row
      display grid
      grid-template-columns 1fr 1fr
      gap 12px

      .field
        min-width 0
        width 100%

    // Le bouton principal est en premier dans le DOM (action par défaut de la touche Entrée),
    // l'ordre visuel est inversé : [Annuler] [Encaisser]
    .button-container
      display flex
      flex-direction row-reverse
      justify-content flex-start
      gap 10px
      margin-top 4px
      .button
        min-width 110px

  .history
    padding-top 14px
    border-top 1px solid #eef0f2

    .section-label
      margin 0 0 6px
      font-size 9.5px
      font-weight 700
      letter-spacing 0.05em
      text-transform uppercase
      color #b4b8bd

    .table
      padding-top 0

@media (max-width 480px)
  .modal-content form .fields-row
    grid-template-columns 1fr
</style>

<style lang="stylus">
@require '~/src/common/style/common.styl';
.add-collection-modal
  > .modal
    width 500px !important
    height auto !important
    max-height 90vh
</style>